import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toastApiError } from "@/lib/errorMessages";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui";
import { PageFrame } from "@/components/common";
import { useAuthStore } from "@/store/authStore";
import {
  adminSettlementKeys,
  adminSettlementService,
  type ReplaceSupplierInvoiceInput,
  type RevolutPayoutMode,
  type SettlementLineRow,
  type SettlementPayeeLinesResponse,
  type SettlementRevolutPayoutResponse,
  type SettlementSummaryEmailResponse,
} from "@/services/adminSettlementService";
import { Permission } from "@/types";
import { resolveSelectionFlow } from "./settlementSelection";
import {
  filterSettlementLines,
  isSettlementLineFinalizedAndPaid,
  linesEligibleForSummaryEmail,
  settlementLineIdsForMetric,
  sortSettlementLinesByEventDate,
  type SettlementFlowFilter,
  type SettlementInvoiceFilter,
  type SettlementMetricFilter,
  type SettlementPaymentFilter,
} from "./settlementUi";
import { SettlementActionSteps } from "./detail/SettlementActionSteps";
import { SettlementLinesSection } from "./detail/SettlementLinesSection";
import { SettlementPayeeHeader } from "./detail/SettlementPayeeHeader";
import { SettlementRevolutBanner } from "./detail/SettlementRevolutBanner";
import { openOrDownload } from "@/lib/downloadBlob";
import {
  isTerminalRevolutStatus,
  selectionAfterReload,
  type Submitting,
} from "./detail/utils";

const EMPTY_LINES: SettlementLineRow[] = [];

type SettlementDocumentKind = "commission" | "supplier";

type SettlementMutationVars =
  | { type: "summary"; lineIds: string[] }
  | { type: "invoice"; note?: string; lineIds: string[] }
  | {
      type: "collect";
      reference: string;
      note?: string;
      lineIds: string[];
    }
  | {
      type: "supplier";
      supplierInvoiceNumber: string;
      supplierInvoiceDate?: string;
      totalCents: number;
      file: File;
      note?: string;
      lineIds: string[];
    }
  | ({ type: "replaceSupplier" } & ReplaceSupplierInvoiceInput)
  | {
      type: "pay";
      reference: string;
      note?: string;
      lineIds: string[];
    }
  | {
      type: "revolut";
      mode: RevolutPayoutMode;
      reference: string;
      note?: string;
      lineIds: string[];
    };

type SettlementMutationResult =
  | { type: "summary"; res: SettlementSummaryEmailResponse }
  | { type: "invoice" }
  | { type: "collect" }
  | { type: "supplier" }
  | { type: "replaceSupplier" }
  | { type: "pay" }
  | { type: "revolut"; res: SettlementRevolutPayoutResponse };

const MUTATION_ERROR_KEYS: Record<Exclude<Submitting, null>, string> = {
  summary: "settlements.detail.steps.summaryError",
  invoice: "settlements.detail.invoicedError",
  collect: "settlements.detail.steps.collectError",
  supplier: "settlements.detail.steps.supplierError",
  replaceSupplier: "settlements.detail.steps.supplierReplaceError",
  pay: "settlements.detail.paidError",
  revolut: "settlements.detail.revolut.error",
};

function documentErrorKey(kind: SettlementDocumentKind): string {
  return kind === "supplier"
    ? "settlements.detail.downloadSupplierInvoiceError"
    : "settlements.detail.downloadFePdfError";
}

export default function SettlementPayeeDetailPage() {
  const { t } = useTranslation("payments");
  const { t: tCommon } = useTranslation("common");
  const { userId = "" } = useParams();
  const queryClient = useQueryClient();
  const hasWrite = useAuthStore((s) => s.hasPermission(Permission.SETTLEMENTS_WRITE));
  const hasSettlementDocumentsRead = useAuthStore((s) =>
    s.hasPermission(Permission.SETTLEMENT_DOCUMENTS_READ),
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [flowFilter, setFlowFilter] = useState<SettlementFlowFilter>("all");
  const [paymentFilter, setPaymentFilter] = useState<SettlementPaymentFilter>("all");
  const [invoiceFilter, setInvoiceFilter] = useState<SettlementInvoiceFilter>("all");
  const [activeMetric, setActiveMetric] = useState<SettlementMetricFilter | null>(null);
  const [documentBusy, setDocumentBusy] = useState(false);

  const linesQuery = useQuery({
    queryKey: adminSettlementKeys.payeeLines(userId),
    queryFn: () => adminSettlementService.getPayeeLines(userId),
    enabled: Boolean(userId),
  });

  const activeRevolutQuery = useQuery({
    queryKey: adminSettlementKeys.activeRevolutPayout(userId),
    queryFn: () => adminSettlementService.getActiveRevolutPayout(userId),
    enabled: Boolean(userId),
  });

  useEffect(() => {
    const loadError = linesQuery.error ?? activeRevolutQuery.error;
    if (loadError) {
      toastApiError(loadError, "generic", t("settlements.detail.loadError"));
    }
  }, [linesQuery.error, activeRevolutQuery.error, t]);

  const payee = linesQuery.data?.payee ?? null;
  const lines = linesQuery.data?.lines ?? EMPTY_LINES;
  const activeRevolutPayout = activeRevolutQuery.data?.payout ?? null;
  const loading = linesQuery.isLoading || activeRevolutQuery.isLoading;
  const isFetching = linesQuery.isFetching || activeRevolutQuery.isFetching;

  const resetFilters = useCallback(() => {
    setFlowFilter("all");
    setPaymentFilter("all");
    setInvoiceFilter("all");
    setActiveMetric(null);
  }, []);

  const refreshDetail = useCallback(
    async (opts: { resetFilters: boolean }) => {
      if (!userId) return;
      if (opts.resetFilters) resetFilters();
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: adminSettlementKeys.payeeLines(userId),
        }),
        queryClient.invalidateQueries({
          queryKey: adminSettlementKeys.activeRevolutPayout(userId),
        }),
        queryClient.invalidateQueries({
          queryKey: adminSettlementKeys.payeeLists(),
        }),
      ]);
      const fresh = queryClient.getQueryData<SettlementPayeeLinesResponse>(
        adminSettlementKeys.payeeLines(userId),
      );
      if (fresh) {
        setSelected((prev) => selectionAfterReload(prev, fresh.lines));
      }
    },
    [queryClient, resetFilters, userId],
  );

  const handleRefresh = useCallback(() => {
    resetFilters();
    void Promise.all([linesQuery.refetch(), activeRevolutQuery.refetch()]).then(
      ([linesResult]) => {
        if (linesResult.data) {
          setSelected((prev) => selectionAfterReload(prev, linesResult.data.lines));
        }
      },
    );
  }, [activeRevolutQuery, linesQuery, resetFilters]);

  useEffect(() => {
    const row = activeRevolutPayout;
    if (!userId || !row || isTerminalRevolutStatus(row.status)) return undefined;

    const tick = async () => {
      try {
        const fresh = await adminSettlementService.getRevolutPayout(row.id);
        if (fresh.status === "COMPLETED") {
          toast.success(t("settlements.detail.revolut.completedToast"));
          queryClient.setQueryData(adminSettlementKeys.activeRevolutPayout(userId), {
            payout: null,
          });
          await refreshDetail({ resetFilters: false });
          return;
        }
        if (isTerminalRevolutStatus(fresh.status)) {
          toast.error(t("settlements.detail.revolut.failedToast"));
          queryClient.setQueryData(adminSettlementKeys.activeRevolutPayout(userId), {
            payout: null,
          });
          return;
        }
        queryClient.setQueryData(adminSettlementKeys.activeRevolutPayout(userId), {
          payout: fresh,
        });
      } catch {
        /* keep polling until terminal */
      }
    };

    const id = window.setInterval(() => void tick(), 5000);
    void tick();
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by payout id/status
  }, [activeRevolutPayout?.id, activeRevolutPayout?.status, refreshDetail, t, userId, queryClient]);

  const actionMutation = useMutation({
    mutationFn: async (vars: SettlementMutationVars): Promise<SettlementMutationResult> => {
      switch (vars.type) {
        case "summary": {
          const res = await adminSettlementService.sendSummaryEmail(userId, {
            line_ids: vars.lineIds,
          });
          return { type: "summary", res };
        }
        case "invoice": {
          await adminSettlementService.markInvoiced(userId, {
            line_ids: vars.lineIds,
            note: vars.note,
          });
          return { type: "invoice" };
        }
        case "collect": {
          await adminSettlementService.recordCollection(userId, {
            line_ids: vars.lineIds,
            reference: vars.reference,
            note: vars.note,
          });
          return { type: "collect" };
        }
        case "supplier": {
          await adminSettlementService.recordSupplierInvoice(userId, {
            line_ids: vars.lineIds,
            file: vars.file,
            supplier_invoice_number: vars.supplierInvoiceNumber,
            supplier_invoice_date: vars.supplierInvoiceDate,
            total_cents: vars.totalCents,
            note: vars.note,
          });
          return { type: "supplier" };
        }
        case "replaceSupplier": {
          await adminSettlementService.replaceSupplierInvoice(vars);
          return { type: "replaceSupplier" };
        }
        case "pay": {
          await adminSettlementService.markPaid(userId, {
            line_ids: vars.lineIds,
            reference: vars.reference,
            note: vars.note,
          });
          return { type: "pay" };
        }
        case "revolut": {
          const res = await adminSettlementService.initiateRevolutPayout(userId, {
            line_ids: vars.lineIds,
            mode: vars.mode,
            reference: vars.reference,
            note: vars.note,
          });
          return { type: "revolut", res };
        }
      }
    },
    onSuccess: async (result) => {
      switch (result.type) {
        case "summary":
          if (result.res.sent) {
            toast.success(t("settlements.detail.steps.summarySuccess"));
            await refreshDetail({ resetFilters: false });
          } else {
            toast.error(
              t("settlements.detail.steps.summarySkipped", {
                detail: result.res.detail ?? "",
              }),
            );
          }
          break;
        case "invoice":
          toast.success(t("settlements.detail.invoicedSuccess"));
          await refreshDetail({ resetFilters: true });
          break;
        case "collect":
          toast.success(t("settlements.detail.steps.collectSuccess"));
          await refreshDetail({ resetFilters: true });
          break;
        case "supplier":
          toast.success(t("settlements.detail.steps.supplierSuccess"));
          await refreshDetail({ resetFilters: true });
          break;
        case "replaceSupplier":
          toast.success(t("settlements.detail.steps.supplierReplaceSuccess"));
          await refreshDetail({ resetFilters: false });
          break;
        case "pay":
          toast.success(t("settlements.detail.paidSuccess"));
          await refreshDetail({ resetFilters: true });
          break;
        case "revolut":
          if (result.res.status === "COMPLETED") {
            toast.success(t("settlements.detail.revolut.completedToast"));
            queryClient.setQueryData(adminSettlementKeys.activeRevolutPayout(userId), {
              payout: null,
            });
            await refreshDetail({ resetFilters: true });
          } else {
            toast.success(t("settlements.detail.revolut.initiated"));
            queryClient.setQueryData(adminSettlementKeys.activeRevolutPayout(userId), {
              payout: result.res,
            });
            await refreshDetail({ resetFilters: false });
          }
          break;
      }
    },
    onError: (error, vars) => {
      toastApiError(error, "generic", t(MUTATION_ERROR_KEYS[vars.type]));
    },
  });

  const submitting: Submitting = actionMutation.isPending
    ? (actionMutation.variables?.type ?? null)
    : null;

  const filteredLines = useMemo(
    () =>
      sortSettlementLinesByEventDate(
        filterSettlementLines(lines, flowFilter, paymentFilter, invoiceFilter),
      ),
    [lines, flowFilter, paymentFilter, invoiceFilter],
  );

  const selectedLines = useMemo(
    () => lines.filter((line) => selected.has(line.id)),
    [lines, selected],
  );
  const selectedLinesReadOnly =
    selectedLines.length > 0 && selectedLines.every(isSettlementLineFinalizedAndPaid);

  const totals = useMemo(() => {
    const currency = payee?.currency ?? "EUR";
    const payable = selectedLines.reduce(
      (sum, line) => (line.payment_status === "UNPAID" ? sum + line.payable_amount_cents : sum),
      0,
    );
    const invoiceable = selectedLines.reduce(
      (sum, line) => (line.invoice_status === "PENDING" ? sum + line.invoiceable_amount_cents : sum),
      0,
    );
    const collectible = selectedLines.reduce(
      (sum, line) =>
        line.collection_status === "UNCOLLECTED" ? sum + line.invoiceable_amount_cents : sum,
      0,
    );
    return { currency, payable, invoiceable, collectible };
  }, [payee?.currency, selectedLines]);

  const { flow, mixedSelection } = useMemo(
    () => resolveSelectionFlow(selectedLines),
    [selectedLines],
  );

  const summaryStats = useMemo(() => {
    const eligible = linesEligibleForSummaryEmail(selectedLines);
    const sentCount = Math.max(0, ...eligible.map((l) => l.summary_email_count));
    const lastSent = eligible
      .map((l) => l.last_summary_email_at)
      .filter((d): d is string => !!d)
      .sort()
      .at(-1);
    return { sentCount, lastSent, eligibleCount: eligible.length };
  }, [selectedLines]);

  const blockingRevolutOpen =
    !!activeRevolutPayout && !isTerminalRevolutStatus(activeRevolutPayout.status);

  const toggleLine = (lineId: string) => {
    setActiveMetric(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const applyLineFilters = (next: {
    flow?: SettlementFlowFilter;
    payment?: SettlementPaymentFilter;
    invoice?: SettlementInvoiceFilter;
  }) => {
    setActiveMetric(null);
    if (next.flow !== undefined) {
      setFlowFilter(next.flow);
      setPaymentFilter(next.payment ?? "all");
      setInvoiceFilter(next.invoice ?? "all");
      return;
    }
    if (next.payment !== undefined) setPaymentFilter(next.payment);
    if (next.invoice !== undefined) setInvoiceFilter(next.invoice);
  };

  const selectLinesForMetric = useCallback(
    async (metric: SettlementMetricFilter) => {
      if (!userId) return;
      try {
        const res = await queryClient.fetchQuery({
          queryKey: adminSettlementKeys.payeeLines(userId),
          queryFn: () => adminSettlementService.getPayeeLines(userId),
          staleTime: 0,
        });
        setFlowFilter("all");
        setPaymentFilter("all");
        setInvoiceFilter("all");
        const ids = settlementLineIdsForMetric(res.lines, metric);
        setSelected(new Set(ids));
        setActiveMetric(metric);
      } catch (error) {
        toastApiError(error, "generic", t("settlements.detail.loadError"));
      }
    },
    [queryClient, t, userId],
  );

  const openSettlementDocument = (
    kind: SettlementDocumentKind,
    documentId: string,
    mode: "view" | "download",
  ) => {
    if (documentBusy) return;
    setDocumentBusy(true);
    void openOrDownload({
      mode,
      baseName:
        kind === "supplier"
          ? `supplier-invoice-${documentId.slice(0, 8)}`
          : `commission-invoice-${documentId.slice(0, 8)}`,
      fetchBlob: () =>
        kind === "supplier"
          ? adminSettlementService.downloadSupplierInvoice(documentId)
          : adminSettlementService.downloadSettlementDocumentPdf(documentId),
    })
      .catch((error: unknown) => {
        toastApiError(error, "generic", t(documentErrorKey(kind)));
      })
      .finally(() => {
        setDocumentBusy(false);
      });
  };

  const handleViewFePdf = (documentId: string) => {
    openSettlementDocument("commission", documentId, "view");
  };

  const handleDownloadFePdf = (documentId: string) => {
    openSettlementDocument("commission", documentId, "download");
  };

  const handleViewSupplierInvoice = (documentId: string) => {
    openSettlementDocument("supplier", documentId, "view");
  };

  const handleDownloadSupplierInvoice = (documentId: string) => {
    openSettlementDocument("supplier", documentId, "download");
  };

  const handleSendSummary = () => {
    if (!userId || selected.size === 0) return;
    const lineIds = linesEligibleForSummaryEmail(selectedLines).map((line) => line.id);
    if (lineIds.length === 0) {
      toast.error(t("settlements.detail.steps.summaryAllInvoiced"));
      return;
    }
    actionMutation.mutate({ type: "summary", lineIds });
  };

  const handleMarkInvoiced = (note?: string) => {
    if (!userId || selected.size === 0) return;
    actionMutation.mutate({
      type: "invoice",
      note,
      lineIds: Array.from(selected),
    });
  };

  const handleRecordCollection = (data: { reference: string; note?: string }) => {
    if (!userId || selected.size === 0) return;
    actionMutation.mutate({
      type: "collect",
      reference: data.reference,
      note: data.note,
      lineIds: Array.from(selected),
    });
  };

  const handleRecordSupplierInvoice = (data: {
    supplierInvoiceNumber: string;
    supplierInvoiceDate?: string;
    totalCents: number;
    file: File;
    note?: string;
  }) => {
    if (!userId || selected.size === 0) return;
    actionMutation.mutate({
      type: "supplier",
      supplierInvoiceNumber: data.supplierInvoiceNumber,
      supplierInvoiceDate: data.supplierInvoiceDate,
      totalCents: data.totalCents,
      file: data.file,
      note: data.note,
      lineIds: Array.from(selected),
    });
  };

  const handleReplaceSupplierInvoice = (data: ReplaceSupplierInvoiceInput) =>
    actionMutation.mutateAsync({ type: "replaceSupplier", ...data });

  const handleMarkPaid = (data: { reference: string; note?: string }) => {
    if (!userId || selected.size === 0) return;
    actionMutation.mutate({
      type: "pay",
      reference: data.reference,
      note: data.note,
      lineIds: Array.from(selected),
    });
  };

  const handleRevolutPayout = (data: {
    mode: RevolutPayoutMode;
    reference: string;
    note?: string;
  }) => {
    if (!userId || selected.size === 0) return;
    if (totals.payable <= 0) return;
    actionMutation.mutate({
      type: "revolut",
      mode: data.mode,
      reference: data.reference,
      note: data.note,
      lineIds: Array.from(selected),
    });
  };

  if (loading && !payee) {
    return (
      <PageFrame
        backLink={{
          fallback: "/admin/payments/settlements",
          children: t("settlements.detail.back"),
        }}
        isLoading
        loadingMessage={t("settlements.detail.loading")}
      />
    );
  }

  const busy = submitting !== null || documentBusy;
  const noSelection = selected.size === 0;

  return (
    <PageFrame
      backLink={{
        fallback: "/admin/payments/settlements",
        children: t("settlements.detail.back"),
      }}
    >
      {payee && (
        <SettlementPayeeHeader
          payee={payee}
          flow={flow}
          activeMetric={activeMetric}
          onSelectMetric={(metric) => void selectLinesForMetric(metric)}
          actions={
            <Button
              type="button"
              variant="outline"
              isLoading={isFetching}
              leftIcon={<RefreshCw className="h-4 w-4" />}
              onClick={handleRefresh}
            >
              {tCommon("refresh")}
            </Button>
          }
        />
      )}

      {hasWrite && blockingRevolutOpen && activeRevolutPayout && (
        <SettlementRevolutBanner payout={activeRevolutPayout} />
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start xl:gap-5">
        <SettlementLinesSection
              lines={filteredLines}
              totalLineCount={lines.length}
              selected={selected}
              flowFilter={flowFilter}
              paymentFilter={paymentFilter}
              invoiceFilter={invoiceFilter}
              hasSettlementDocumentsRead={hasSettlementDocumentsRead}
              busy={busy}
              onToggleLine={toggleLine}
              onSelectionChange={setSelected}
              onFlowFilterChange={(filter) => applyLineFilters({ flow: filter })}
              onPaymentFilterChange={(filter) => applyLineFilters({ payment: filter })}
              onInvoiceFilterChange={(filter) => applyLineFilters({ invoice: filter })}
              onSelectAll={() => {
                setActiveMetric(null);
                setSelected(new Set(filteredLines.map((line) => line.id)));
              }}
              onDeselectAll={() => {
                setActiveMetric(null);
                setSelected(new Set());
              }}
              onViewFePdf={handleViewFePdf}
              onDownloadFePdf={handleDownloadFePdf}
              onViewSupplierInvoice={handleViewSupplierInvoice}
              onDownloadSupplierInvoice={handleDownloadSupplierInvoice}
            />

        <SettlementActionSteps
          flow={flow}
          selectedLines={selectedLines}
          hasWrite={hasWrite}
          busy={busy}
          noSelection={noSelection}
          readOnly={selectedLinesReadOnly}
          mixedSelection={mixedSelection}
          selectedCount={selected.size}
          submitting={submitting}
          totals={totals}
          summaryStats={summaryStats}
          payee={payee}
          blockingRevolutOpen={blockingRevolutOpen}
          onSendSummary={handleSendSummary}
          onRecordCollection={handleRecordCollection}
          onRecordSupplierInvoice={handleRecordSupplierInvoice}
          onReplaceSupplierInvoice={handleReplaceSupplierInvoice}
          onViewSupplierInvoice={
            hasSettlementDocumentsRead ? handleViewSupplierInvoice : undefined
          }
          onDownloadSupplierInvoice={
            hasSettlementDocumentsRead ? handleDownloadSupplierInvoice : undefined
          }
          onMarkInvoiced={handleMarkInvoiced}
          onMarkPaid={handleMarkPaid}
          onRevolutPayout={handleRevolutPayout}
        />
      </div>
    </PageFrame>
  );
}
